import type { DetectionKind } from '@/core/detection/types';
import { isSameVideo, type VideoIdentity } from '@/core/media/same-video';
import { createDefaultRecipe, parsePrivacy, type EditRecipe } from '@/core/project/recipe';
import { layerAssetId, normalizeLayer } from '@/core/project/layers';
import { parseOutputFormat } from '@/core/project/output-format';
import { parsePlacement } from '@/core/project/placement';
import { parseSizeLimit } from '@/core/project/size-target';
import { parseTransition } from '@/core/project/transition';
import { effectiveClips, PRIMARY_ASSET, type AssetId } from '@/core/project/sequence';
import type { Track } from '@/core/tracking/track';
import type { AsrQuality, CaptionLanguage } from '@/core/captions/types';
import type { ScanPrecision } from '@/core/detection/scan-plan';
import type { MediaInfo, SoundInfo } from '@/engine/types';
import type { AssetKind } from '../assets';
import type { Coverage } from '../privacy-scans';

/**
 * An edit kept on this device so it can be picked up again later. The videos,
 * pictures and sounds themselves are not copied (that would double the space
 * they take): the project keeps what identifies each file and, where the
 * browser gives one, a handle to read it through again (lib/file-handles.ts).
 * Only files it can't reach that way are asked for, and matched up by itself.
 * A screen recording made here is the exception: it already lives on the
 * device, so the project keeps it there.
 */
export const PROJECT_VERSION = 1;

/** What identifies an imported file, and what was learned about it. */
export interface SavedAsset {
  readonly id: AssetId;
  readonly kind: AssetKind;
  readonly name: string;
  readonly size: number;
  readonly lastModified: number;
  readonly type: string;
  readonly width: number;
  readonly height: number;
  readonly durationSec: number;
  readonly video: MediaInfo | null;
  readonly sound: SoundInfo | null;
  readonly hasAudio: boolean;
  readonly removed: boolean;
  /** Where the file is on the disk (Chromium on computers), to read it again without asking; absent elsewhere. */
  readonly handle?: FileSystemFileHandle;
}

export interface SavedProject {
  readonly version: number;
  readonly id: string;
  /** The opened video's file name. */
  readonly name: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Length of the edit before cuts, and its number of clips (for the list). */
  readonly durationSec: number;
  readonly clipCount: number;
  /** A small picture of the opened video, for the list. */
  readonly poster: Blob | null;
  readonly recipe: EditRecipe;
  readonly assets: readonly SavedAsset[];
  readonly assetOrder: readonly AssetId[];
  readonly privacy: {
    readonly tracksByAsset: Readonly<Record<AssetId, readonly Track[]>>;
    readonly scanned: Readonly<Record<DetectionKind, readonly AssetId[]>>;
    /** The parts of each video that were scanned; absent in projects saved before only parts were. */
    readonly covered?: Coverage;
    /** Kinds that were looked for (their results are real, even when empty). */
    readonly completed: readonly DetectionKind[];
    readonly precision: ScanPrecision;
    readonly thumbnails: Readonly<Record<string, Blob>>;
  };
  readonly areaThumbnails: Readonly<Record<string, Blob>>;
  readonly transcription: {
    readonly language: CaptionLanguage;
    readonly quality: AsrQuality;
    readonly spoken: string | null;
    readonly captioned: readonly AssetId[];
    readonly completed: boolean;
  };
  /** The recipe the edit was started with. */
  readonly presetId: string | null;
  /** The screen recording the edit came from, kept on the device with the project. */
  readonly recordingId: string | null;
}

/** The list on the home page needs only this much. */
export type ProjectSummary = Pick<SavedProject, 'id' | 'name' | 'createdAt' | 'updatedAt' | 'durationSec' | 'clipCount' | 'poster' | 'recordingId'>;

/** The opened recipe, with defaults for anything a project saved by an older version didn't have yet. */
export function restoredRecipe(project: Pick<SavedProject, 'recipe'>): EditRecipe {
  const recipe = { ...createDefaultRecipe(), ...project.recipe };
  return {
    ...recipe,
    layers: (recipe.layers ?? []).map(normalizeLayer),
    privacy: parsePrivacy(recipe.privacy),
    transition: parseTransition(recipe.transition),
    output: parseOutputFormat(recipe.output),
    sizeLimit: parseSizeLimit(recipe.sizeLimit),
    placement: parsePlacement(recipe.placement),
  };
}

/**
 * Files the edit can't open without: every video its clips play and every
 * file its layers show. Imports that aren't used anywhere are not needed.
 */
export function neededAssets(project: Pick<SavedProject, 'recipe' | 'assets'>): SavedAsset[] {
  const recipe = restoredRecipe(project);
  const primary = project.assets.find((asset) => asset.id === PRIMARY_ASSET);
  const clips = effectiveClips(recipe.sequence.clips, primary ? { durationSec: primary.durationSec, width: primary.width, height: primary.height } : undefined);
  const used = new Set<AssetId>([PRIMARY_ASSET, ...clips.map((clip) => clip.assetId), ...recipe.layers.flatMap((layer) => layerAssetId(layer) ?? [])]);
  return project.assets.filter((asset) => used.has(asset.id));
}

/** Tells what a picked file is as a video (its size and length), or null when it isn't one this browser opens. */
export type Identify = (file: File) => Promise<VideoIdentity | null>;

/**
 * Pairs picked files with the project's assets: the same name and size first
 * (the file as it was), then, for videos, the same picture size and length (a
 * gallery's re-saved copy under another name), then pictures and sounds of the
 * same size. Each file serves one asset.
 */
export async function matchFiles(assets: readonly SavedAsset[], files: readonly File[], identify: Identify): Promise<Map<AssetId, File>> {
  const matched = new Map<AssetId, File>();
  const free = new Set(files);
  const take = (asset: SavedAsset, file: File) => {
    matched.set(asset.id, file);
    free.delete(file);
  };
  for (const asset of assets) {
    const exact = [...free].find((file) => file.name === asset.name && file.size === asset.size);
    if (exact) take(asset, exact);
  }
  const identities = new Map<File, VideoIdentity | null>();
  for (const asset of assets) {
    if (matched.has(asset.id)) continue;
    if (asset.kind === 'video' && asset.video) {
      for (const file of [...free]) {
        if (!identities.has(file)) identities.set(file, await identify(file).catch(() => null));
        const identity = identities.get(file);
        if (identity && isSameVideo(asset.video, identity)) {
          take(asset, file);
          break;
        }
      }
    } else {
      const same = [...free].find((file) => file.size === asset.size);
      if (same) take(asset, same);
    }
  }
  return matched;
}

/** How old a project is, in the largest unit that reads naturally ("3 hours ago"). */
export function age(updatedAt: number, now: number): { readonly value: number; readonly unit: Intl.RelativeTimeFormatUnit } {
  const seconds = Math.max(0, (now - updatedAt) / 1000);
  if (seconds < 60) return { value: 0, unit: 'minute' };
  if (seconds < 3600) return { value: Math.floor(seconds / 60), unit: 'minute' };
  if (seconds < 86_400) return { value: Math.floor(seconds / 3600), unit: 'hour' };
  if (seconds < 7 * 86_400) return { value: Math.floor(seconds / 86_400), unit: 'day' };
  return { value: Math.floor(seconds / (7 * 86_400)), unit: 'week' };
}
